<template>
	<div class="stepSummary">
		<header class="stepSummary__header">
			<h2 class="stepSummary__title">{{ title }}</h2>
			<p class="stepSummary__caption">{{ $t('setupWizard_summary_caption') }}</p>
		</header>

		<dl class="stepSummary__list">
			<div
				class="stepSummary__item"
				v-if="workModeAvailable"
			>
				<dt class="stepSummary__label">{{ $t('setupWizard_summary_work_mode') }}</dt>
				<dd class="stepSummary__value">{{ workModeSummary }}</dd>
			</div>
			<div class="stepSummary__item">
				<dt class="stepSummary__label">{{ $t('setupWizard_summary_theme') }}</dt>
				<dd class="stepSummary__value">{{ themeSummary }}</dd>
			</div>
			<div class="stepSummary__item">
				<dt class="stepSummary__label">{{ $t('setupWizard_summary_buttons') }}</dt>
				<dd class="stepSummary__value">{{ buttonsSummary }}</dd>
			</div>
			<div class="stepSummary__item">
				<dt class="stepSummary__label">{{ $t('setupWizard_summary_layout') }}</dt>
				<dd class="stepSummary__value">{{ layoutSummary }}</dd>
			</div>
			<div class="stepSummary__item">
				<dt class="stepSummary__label">{{ $t('setupWizard_summary_features') }}</dt>
				<dd class="stepSummary__value">{{ featuresSummary }}</dd>
			</div>
		</dl>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';

import { useBreakpoint } from 'src/composables/useBreakpoint';
import { useUserStore } from 'src/stores/user';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import { PRIMARY_COLORS, SURFACE_COLORS } from 'src/theme/themeConfig';

import { canUseWorkspaceTabsSettings } from 'src/util/platform/browserInteractionCapabilities';

export default defineComponent({
	name: 'StepSummary',
	setup() {
		const { t } = useI18n();
		const store = useUserInterfaceSettingsStore();
		const userStore = useUserStore();
		const { width } = useBreakpoint();

		const separator = computed<string>(() => t('setupWizard_summary_separator'));

		const workModeAvailable = computed<boolean>(() => canUseWorkspaceTabsSettings(width.value));

		const workModeSummary = computed<string>(() =>
			t(store.workspaceTabsEnabled ? 'setupWizard_work_mode_tabs_title' : 'setupWizard_work_mode_focus_title')
		);

		const title = computed<string>(() => {
			const user = userStore.authenticatedUserInformation;
			const firstName = user?.firstName?.trim() ?? '';
			const lastName = user?.lastName?.trim() ?? '';
			const fullName = [firstName, lastName].filter(Boolean).join(' ');
			const namePart = fullName !== '' ? `, ${fullName}` : '';
			return t('setupWizard_summary_title', { name: namePart });
		});

		const themeModeKeyByValue: Record<'auto' | 'dark' | 'light', string> = {
			auto: 'settings_theme_toggle_mode_automatic',
			dark: 'settings_theme_toggle_mode_dark',
			light: 'settings_theme_toggle_mode_light',
		};

		const themeSummary = computed<string>(() => {
			const modeLabel = t(themeModeKeyByValue[store.themeMode]);
			const primary =
				store.themePrimaryColor === 'custom'
					? t('settings_theme_custom_color_label')
					: (PRIMARY_COLORS.find((color) => color.name === store.themePrimaryColor)?.label ??
						store.themePrimaryColor);
			const surface =
				SURFACE_COLORS.find((surface) => surface.name === store.themeSurfaceColor)?.label ??
				store.themeSurfaceColor;

			return [modeLabel, primary, surface].join(separator.value);
		});

		const buttonsSummary = computed<string>(() => {
			const view = t(
				store.shouldUseTheCompactButtonView
					? 'settings_theme_button_density_full_label'
					: 'settings_theme_button_density_compact_label'
			);
			const colors = t(
				store.shouldUseUniColorButtons
					? 'settings_theme_button_color_mode_uni_label'
					: 'settings_theme_button_color_mode_multi_label'
			);
			const icons = t(
				store.menuItemsSolid
					? 'settings_theme_icon_style_solid_label'
					: 'settings_theme_icon_style_regular_label'
			);

			return [view, colors, icons].join(separator.value);
		});

		const navigationKeyByValue: Record<'sidebar' | 'topbar', string> = {
			sidebar: 'settings_layout_navigation_mode_sidebar',
			topbar: 'settings_layout_navigation_mode_topbar',
		};

		const layoutSummary = computed<string>(() => {
			const navigation = t(navigationKeyByValue[store.navigationMode]);
			const fontSize = `${store.appFontSizePt}pt`;

			return [navigation, fontSize].join(separator.value);
		});

		const featuresSummary = computed<string>(() => {
			const parts: string[] = [];
			parts.push(`${t('settings_layout_timer_enabled_label')} ${store.timerEnabled ? '✓' : '✗'}`);
			parts.push(`${t('settings_layout_spotlight_search_label')} ${store.spotlightSearchEnabled ? '✓' : '✗'}`);

			return parts.join(separator.value);
		});

		return {
			buttonsSummary,
			featuresSummary,
			layoutSummary,
			themeSummary,
			title,
			workModeAvailable,
			workModeSummary,
		};
	},
});
</script>

<style lang="scss" scoped>
.stepSummary {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.stepSummary__header {
	margin-bottom: 0;
}

.stepSummary__title {
	font-size: var(--text-xl);
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 0 0 0.125rem;
}

.stepSummary__caption {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	margin: 0;
}

.stepSummary__list {
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	margin: 0;
}

.stepSummary__item {
	align-items: baseline;
	display: grid;
	gap: 1rem;
	grid-template-columns: 6.5rem minmax(0, 1fr);
}

.stepSummary__label {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
	font-weight: 500;
	letter-spacing: 0.02em;
	margin: 0;
	text-transform: uppercase;
}

.stepSummary__value {
	color: var(--p-text-color);
	font-size: var(--text-sm-plus);
	margin: 0;
}
</style>
